import React, { useCallback, useEffect, useState } from 'react';
import { Chip, FormHelperText } from '@material-ui/core';
import Autocomplete, { createFilterOptions } from '@material-ui/lab/Autocomplete';

const OPTION_LIMIT = 10;

const filterOptions = createFilterOptions({
    matchFrom: 'any',
    limit: OPTION_LIMIT
});

/**
 * Check if a value is an object with id key exists or not
 * @param e
 * @returns {boolean}
 */
function isValueObjectWithId(e) {
    return typeof e === 'object' && e !== null && typeof e.id !== 'undefined';
}

/**
 * Map autocomplete values to ids
 * @param oneValue
 * @returns {*}
 */
function mapValueToId(oneValue) {
    return isValueObjectWithId(oneValue) ? oneValue.id : oneValue;
}

/**
 * Merge options + optionsCache with one list, where the ids are unique
 * @param options
 * @param optionsCache
 * @returns {*[]}
 */
function createUniqueOptionsFromOptionsAndCache(options, optionsCache) {
    // unique the options
    const uniqueIds = [];
    const uniqueOptions = [];
    options.forEach(e => {
        if (uniqueIds.includes(e.id)) {
            return;
        }
        uniqueIds.push(e.id);
        uniqueOptions.push(e);
    });
    if (optionsCache !== null) {
        optionsCache.forEach(e => {
            if (!e || uniqueIds.includes(e.id)) {
                return;
            }
            uniqueIds.push(e.id);
            uniqueOptions.push(e);
        });
    }
    return uniqueOptions;
}

/**
 * AutocompleteSelect for general use
 * @param value a list of IDs (or a single id)
 * @param setValue
 * @param autocompleteCall async function with (search, limit option)
 * @param initCall array or async function with list of ids
 * @param children
 * @param multiple true if multiple items can be selected
 * @param autocompleteProps
 * @param InputLabelProps
 * @param error
 * @param helperText
 * @returns {JSX.Element|null}
 * @constructor
 */
const AutocompleteSelect = ({
    value,
    setValue,
    autocompleteCall,
    initCall,
    children,
    multiple = false,
    error = false,
    InputLabelProps = {},
    InputProps = {},
    allowCustom = false,
    helperText = '',
    renderInput: customRenderInput,
    ...autocompleteProps
}) => {
    const [inputValue, setInputValue] = useState('');
    const [options, setOptions] = useState([]);
    // if initCall is array, use it as soon as we can
    const [optionsCache, setOptionsCache] = useState(Array.isArray(initCall) ? initCall : null);
    const [initCallDidRun, setInitCallDidRun] = useState(Array.isArray(initCall));

    // make sure we have initcall + autocomplete call
    if (!autocompleteCall) {
        console.error('You must specify an autocompleteCall for AutocompleteSelect');
    }
    if (!initCall) {
        console.error(
            'You must specify an initCall for AutocompleteSelect or provide a predefined list!'
        );
    }

    // on initial load try to resolve the already selected options
    const cacheLoaded = optionsCache !== null;
    useEffect(() => {
        // if we already have something in the cache, do not load it
        if (cacheLoaded || Array.isArray(initCall)) {
            return;
        }

        // if freeSolo is enabled, skip initCall since custom strings have no DB record to resolve
        if (allowCustom && (typeof value === 'string' || value == null || value === '')) {
            setInitCallDidRun(true);
            return;
        }

        // no need to resolve nothing
        if (multiple && Array.isArray(value) && value.length === 0) {
            setInitCallDidRun(true);
            return;
        }

        // even if the call is sync, let's make it a promise
        Promise.resolve(initCall(multiple ? value : [value]))
            .then(setOptionsCache)
            .then(() => setInitCallDidRun(true))
            .catch(console.error);
    }, [cacheLoaded, initCall, setOptionsCache, setInitCallDidRun, allowCustom, multiple, value]);

    // on input change, make the search call
    useEffect(() => {
        // I know, we should not search for an empty string,
        // but it will load some values into the dropdown
        // uncomment if you think otherwise
        // if (inputValue===''){ return; }

        // even if the call is sync, let's make it a promise
        Promise.resolve(autocompleteCall(inputValue, OPTION_LIMIT)).then(setOptions);
    }, [inputValue, setOptions]);

    // handle changes in the list of items (with cache fill up)
    const onChange = useCallback(
        (event, newValue) => {
            // define a temporary cache to provide it as helper function
            // since it might get updated in the next render cycle
            let newOptionCache = [];

            // from newValue create id list (or single id) + update option cache
            let mappedIds = null;
            if (!multiple) {
                mappedIds = mapValueToId(newValue);
                newOptionCache = isValueObjectWithId(newValue) ? [newValue] : [];
            } else {
                mappedIds = newValue.map(mapValueToId);
                newOptionCache = newValue.map(e =>
                    isValueObjectWithId(e)
                        ? e
                        : optionsCache.find(c => c && `${c.id}` === `${mapValueToId(e)}`)
                );
            }
            setOptionsCache(newOptionCache);
            setValue(
                mappedIds,
                // helper function passed down as the 2nd parameter in setValue
                id => {
                    // as newOptionCache isn't coming from setOptionsCache, this can run before setOptionsCache resolves
                    const item = newOptionCache.find(e => `${e.id}` === `${id}`);
                    return item;
                }
            );
        },
        [setOptionsCache, setValue, optionsCache]
    );

    if (!initCallDidRun) {
        return null;
    }

    return (
        <>
            <Autocomplete
                freeSolo={allowCustom}
                options={createUniqueOptionsFromOptionsAndCache(options, optionsCache)}
                getOptionLabel={option => {
                    // option can be an object that we did find
                    if (typeof option === 'object') {
                        return `${option.name}`;
                    }

                    // can be just the id, on initial load, when we have some id as input, but cannot resolve from optionsCache
                    const item = optionsCache && optionsCache.find(e => `${e.id}` === `${option}`);
                    if (item) {
                        return item.name;
                    }
                    // or we are fighting a race condition during the first render
                    if (Array.isArray(initCall) && !optionsCache) {
                        const initItem = initCall.find(e => `${e.id}` === `${option}`);
                        if (initItem) {
                            return initItem.name;
                        }
                    }
                    // or something else...
                    return `${option}`;
                }}
                filterOptions={filterOptions}
                value={value}
                getOptionSelected={(option, currentValue) => `${option.id}` === `${currentValue}`}
                onChange={onChange}
                renderTags={(tags, getTagProps) =>
                    tags.map((option, index) => {
                        // items should be in the cache after initial load
                        const itemInOptions =
                            optionsCache !== null &&
                            optionsCache.find(e => e && `${e.id}` === `${option}`);
                        return (
                            <Chip
                                label={itemInOptions ? itemInOptions.name : `#${option}`}
                                {...getTagProps({ index })}
                            />
                        );
                    })
                }
                inputValue={inputValue}
                onInputChange={(event, newInputValue) => {
                    setInputValue(newInputValue);
                    if (allowCustom && !multiple) {
                        setValue(newInputValue || '', () => null);
                    }
                }}
                renderInput={params => {
                    if (customRenderInput) {
                        return customRenderInput(params);
                    }
                    return React.cloneElement(children, {
                        ...params,
                        placeholder: 'Start typing to search...',
                        InputLabelProps: {
                            ...params.InputLabelProps,
                            ...InputLabelProps
                        },
                        InputProps: {
                            style: {
                                ...InputProps.style
                            },
                            ...params.InputProps
                        }
                    });
                }}
                // keep this one here so everything can be overwritten by props
                {...autocompleteProps}
                multiple={multiple}
            />
            {error && <FormHelperText error={error}>{helperText}</FormHelperText>}
        </>
    );
};

export default AutocompleteSelect;
